// Coaching trigger system for contextual essay feedback

export const coachingTriggers = {
  // Word count based triggers
  wordThreshold: {
    threshold: 30,
    delay: 2000, // 2 second delay after user stops typing
    messages: [
      "Great start! Now add one specific detail to make this scene more vivid.",
      "I can feel the emotion here. What did this moment look like, sound like, or smell like?",
      "This is unique to you - expand on what made your approach different from others.",
      "Strong writing! Consider adding a specific example to illustrate your point.",
      "You're building momentum. What was the most surprising part of this experience?"
    ]
  },

  // Generic language patterns to detect
  genericLanguage: {
    patterns: [
      'it was hard',
      'very challenging',
      'i learned',
      'i realized',
      'it was difficult',
      'very important',
      'really good',
      'pretty bad',
      'kind of',
      'sort of',
      'a lot of'
    ],
    message: "This feels generic. Show me the specific moment instead of telling me about it.",
    alternativeMessages: [
      "Replace this generic phrase with a specific detail or example.",
      "What exactly made it [hard/challenging/important]? Be specific.",
      "Instead of saying you 'learned' or 'realized', show the moment it clicked."
    ]
  },

  // Sensory detail detection
  needsDetails: {
    threshold: 50, // words without sensory language
    sensoryWords: [
      'saw', 'heard', 'felt', 'smelled', 'tasted',
      'looked', 'sounded', 'touched', 'watched',
      'cold', 'warm', 'bright', 'dark', 'loud', 'quiet',
      'rough', 'smooth', 'sharp', 'soft'
    ],
    message: "I want to be there with you. What did you see, hear, feel, or smell in this moment?",
    alternativeMessages: [
      "Add sensory details - what did this moment look, sound, or feel like?",
      "Help me visualize this scene. What specific details do you remember?",
      "Ground this moment in concrete details. What would a camera capture?"
    ]
  },

  // Cliché detection
  clichePatterns: {
    patterns: [
      'taught me',
      'changed my life',
      'eye-opening',
      'life-changing',
      'never be the same',
      'learned the importance of',
      'realized the value of',
      'at the end of the day',
      'when all was said and done'
    ],
    message: "This phrase is overused in essays. Find a fresh way to express this idea.",
    suggestion: "Instead of a cliché, describe the specific change or realization."
  },

  // Passive voice detection
  passiveVoice: {
    patterns: [
      'was done',
      'were made',
      'was given',
      'were told',
      'was shown',
      'been done',
      'being made'
    ],
    message: "This sounds passive. Make yourself the active subject of the sentence.",
    suggestion: "Rewrite with you as the subject doing the action."
  },

  // Step-specific triggers
  stepSpecific: {
    opening_scene: {
      trigger: (text, wordCount) => wordCount > 20 && !text.includes('"') && !text.includes("'"),
      message: "Consider starting with dialogue or a specific moment to draw readers in immediately."
    },
    the_real_stakes: {
      trigger: (text) => !text.match(/\b(because|since|meant|mattered)\b/i),
      message: "Explain WHY this mattered to you specifically. What was really at stake?"
    },
    your_problem_solving: {
      trigger: (text) => text.split('.').length < 3 && text.length > 100,
      message: "Break this into shorter sentences to show your thought process step by step."
    },
    proof_of_growth: {
      trigger: (text) => !text.match(/\b(now|recently|today|last)\b/i),
      message: "Include a recent example that proves this growth. When did you last use this skill?"
    }
  }
};

// Main analysis function
export const analyzeForCoaching = (text, wordCount, currentStepId) => {
  const lowercaseText = text.toLowerCase();
  const triggers = [];

  // Check word threshold for encouragement
  if (wordCount >= coachingTriggers.wordThreshold.threshold && wordCount % 50 === 0) {
    const messages = coachingTriggers.wordThreshold.messages;
    triggers.push({
      type: 'encouragement',
      message: messages[Math.floor(Math.random() * messages.length)],
      delay: coachingTriggers.wordThreshold.delay
    });
  }

  // Check for generic language
  const hasGenericLanguage = coachingTriggers.genericLanguage.patterns.some(pattern => 
    lowercaseText.includes(pattern)
  );
  
  if (hasGenericLanguage) {
    const messages = [
      coachingTriggers.genericLanguage.message,
      ...coachingTriggers.genericLanguage.alternativeMessages
    ];
    triggers.push({
      type: 'suggestion',
      message: messages[Math.floor(Math.random() * messages.length)],
      immediate: true
    });
  }

  // Check for sensory details (only after 50+ words)
  if (wordCount > coachingTriggers.needsDetails.threshold) {
    const hasSensoryDetails = coachingTriggers.needsDetails.sensoryWords.some(word => 
      lowercaseText.includes(word)
    );
    
    if (!hasSensoryDetails) {
      const messages = [
        coachingTriggers.needsDetails.message,
        ...coachingTriggers.needsDetails.alternativeMessages
      ];
      triggers.push({
        type: 'suggestion',
        message: messages[Math.floor(Math.random() * messages.length)],
        delay: 3000
      });
    }
  }

  // Check for clichés
  const hasCliche = coachingTriggers.clichePatterns.patterns.some(pattern => 
    lowercaseText.includes(pattern)
  );
  
  if (hasCliche) {
    triggers.push({
      type: 'warning',
      message: coachingTriggers.clichePatterns.message,
      suggestion: coachingTriggers.clichePatterns.suggestion
    });
  }

  // Check for passive voice
  const hasPassiveVoice = coachingTriggers.passiveVoice.patterns.some(pattern => 
    lowercaseText.includes(pattern)
  );
  
  if (hasPassiveVoice && wordCount > 30) {
    triggers.push({
      type: 'suggestion',
      message: coachingTriggers.passiveVoice.message,
      suggestion: coachingTriggers.passiveVoice.suggestion
    });
  }

  // Check step-specific triggers
  if (currentStepId && coachingTriggers.stepSpecific[currentStepId]) {
    const stepTrigger = coachingTriggers.stepSpecific[currentStepId];
    if (stepTrigger.trigger(text, wordCount)) {
      triggers.push({
        type: 'suggestion',
        message: stepTrigger.message,
        delay: 2000
      });
    }
  }

  // Return the highest priority trigger
  const immediateTrigger = triggers.find(t => t.immediate);
  if (immediateTrigger) return immediateTrigger;
  
  const warningTrigger = triggers.find(t => t.type === 'warning');
  if (warningTrigger) return warningTrigger;
  
  const suggestionTrigger = triggers.find(t => t.type === 'suggestion');
  if (suggestionTrigger) return suggestionTrigger;
  
  return triggers[0] || null;
};

// Helper function to get quality indicators based on essay analysis
export const getQualityIndicators = (text, wordCount) => {
  const indicators = [];
  const lowercaseText = text.toLowerCase();
  
  // Check for strong opening (dialogue or specific moment)
  const hasStrongOpening = text.includes('"') || text.includes("'") || 
                           text.match(/^\s*(The|My|I|We|They|She|He)\s+\w+/);
  indicators.push({
    label: 'Strong opening',
    status: hasStrongOpening && wordCount > 20 ? 'good' : wordCount > 20 ? 'needs-work' : 'neutral'
  });
  
  // Check for personal voice (first person usage)
  const firstPersonCount = (text.match(/\b(I|me|my|mine|myself|we|our|ours)\b/gi) || []).length;
  const hasPersonalVoice = firstPersonCount > wordCount / 50; // At least 1 per 50 words
  indicators.push({
    label: 'Personal voice',
    status: hasPersonalVoice ? 'good' : wordCount > 50 ? 'needs-work' : 'neutral'
  });
  
  // Check for specific details
  const hasSensoryDetails = coachingTriggers.needsDetails.sensoryWords.some(word => 
    lowercaseText.includes(word)
  );
  indicators.push({
    label: 'Specific details',
    status: hasSensoryDetails ? 'good' : wordCount > 100 ? 'warning' : 'neutral'
  });
  
  // Check for flow and transitions
  const transitionWords = ['however', 'therefore', 'meanwhile', 'subsequently', 'moreover', 
                          'furthermore', 'although', 'despite', 'because', 'since'];
  const hasTransitions = transitionWords.some(word => lowercaseText.includes(word));
  indicators.push({
    label: 'Flow & transitions',
    status: hasTransitions && wordCount > 200 ? 'good' : 
            wordCount > 200 ? 'needs-work' : 'neutral'
  });
  
  return indicators;
};

export default {
  coachingTriggers,
  analyzeForCoaching,
  getQualityIndicators
};